"use client";

import { useCallback, useEffect, useState } from "react";

import {
  useGlobalQueryParams as useGlobalQueryParamsDefault,
  useNoopGlobalQueryParams,
} from "./queryParams/useGlobalQueryParams";
import {
  GLOBAL_UNIT_GROUP_KEY,
  GlobalFilterFieldsEnum,
  getGlobalFilterKey,
} from "../utils/queryParams/globalQueryParams";

export type UnitSelection =
  | { type: "all" }
  | { type: "unit"; unitId: string }
  | { type: "group"; groupSlug: string };

const STORAGE_UNIT = "selectedUnit";
const STORAGE_GROUP = "selectedUnitGroup";
const STORAGE_DISMISSED = "unitSelectionDismissed";

interface UseUnitSelectionOptions {
  useQueryParams?: typeof useGlobalQueryParamsDefault;
  restoreStoredChoice?: boolean;
}

export function useUnitSelection({
  useQueryParams,
  restoreStoredChoice = true,
}: UseUnitSelectionOptions = {}) {
  const queryParams = useQueryParams
    ? useQueryParams()
    : useNoopGlobalQueryParams();
  const { getFilterValue, getRawParam, updateParams } = queryParams;

  const unitKey = getGlobalFilterKey(GlobalFilterFieldsEnum.unit);

  const urlUnit = getFilterValue(GlobalFilterFieldsEnum.unit);
  const urlGroup = getRawParam?.(GLOBAL_UNIT_GROUP_KEY) ?? null;

  const applySelection = useCallback(
    (selected: UnitSelection, alsoUpdate?: (params: URLSearchParams) => void) => {
      const nextUnit = selected.type === "unit" ? selected.unitId : null;
      const nextGroup = selected.type === "group" ? selected.groupSlug : null;

      if (!alsoUpdate && nextUnit === urlUnit && nextGroup === urlGroup) return;

      updateParams((params) => {
        params.delete(unitKey);
        params.delete(GLOBAL_UNIT_GROUP_KEY);

        if (nextUnit) params.set(unitKey, nextUnit);
        if (nextGroup) params.set(GLOBAL_UNIT_GROUP_KEY, nextGroup);

        alsoUpdate?.(params);
      });
    },
    [updateParams, unitKey, urlUnit, urlGroup],
  );

  const [hasStoredChoice, setHasStoredChoice] = useState<boolean | null>(null);

  const selection: UnitSelection = urlGroup
    ? { type: "group", groupSlug: urlGroup }
    : urlUnit
      ? { type: "unit", unitId: urlUnit }
      : { type: "all" };

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!restoreStoredChoice) return;

    const storedUnit = localStorage.getItem(STORAGE_UNIT);
    const storedGroup = localStorage.getItem(STORAGE_GROUP);
    const dismissed = localStorage.getItem(STORAGE_DISMISSED);

    setHasStoredChoice(Boolean(storedUnit || storedGroup || dismissed));

    if (urlGroup) {
      localStorage.setItem(STORAGE_GROUP, urlGroup);
      localStorage.removeItem(STORAGE_UNIT);
      return;
    }

    if (urlUnit) {
      localStorage.setItem(STORAGE_UNIT, urlUnit);
      localStorage.removeItem(STORAGE_GROUP);
      return;
    }

    if (storedGroup) {
      applySelection({ type: "group", groupSlug: storedGroup });
      return;
    }

    if (storedUnit) {
      applySelection({ type: "unit", unitId: storedUnit });
    }
  }, [urlUnit, urlGroup, restoreStoredChoice, applySelection]);

  const selectUnit = useCallback(
    (unitId: string) => {
      if (typeof window === "undefined") return;

      localStorage.setItem(STORAGE_UNIT, unitId);
      localStorage.removeItem(STORAGE_GROUP);
      setHasStoredChoice(true);

      applySelection({ type: "unit", unitId });
    },
    [applySelection],
  );

  const selectGroup = useCallback(
    (groupSlug: string) => {
      if (typeof window === "undefined") return;

      localStorage.setItem(STORAGE_GROUP, groupSlug);
      localStorage.removeItem(STORAGE_UNIT);
      setHasStoredChoice(true);

      applySelection({ type: "group", groupSlug });
    },
    [applySelection],
  );

  const selectAll = useCallback(
    (alsoUpdate?: (params: URLSearchParams) => void) => {
      if (typeof window === "undefined") return;

      localStorage.removeItem(STORAGE_UNIT);
      localStorage.removeItem(STORAGE_GROUP);
      localStorage.setItem(STORAGE_DISMISSED, "true");
      setHasStoredChoice(true);

      applySelection({ type: "all" }, alsoUpdate);
    },
    [applySelection],
  );

  const dismiss = useCallback(() => {
    if (typeof window === "undefined") return;

    localStorage.setItem(STORAGE_DISMISSED, "true");
    setHasStoredChoice(true);
  }, []);

  return {
    selection,
    hasStoredChoice,
    selectUnit,
    selectGroup,
    selectAll,
    dismiss,
  };
}
